import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, User } from "lucide-react";
import { VOCI_MENU } from "@/lib/menuLaterale";

// Sprint CRM-1 — elementi comuni delle pagine CRM (regole in /app/memory/crm/REGOLE_CRM.md)
// Sprint CRM-1.1 — «In corso» è l'etichetta dello stato «aperta» (il valore salvato non cambia), ovunque
export const STATI_OPP = { aperta: "In corso", abbandonata: "Abbandonata", persa: "Persa", vinta: "Vinta" };
export const STATI_PREDEFINITI = ["aperta", "vinta"];
// Stile dei collegamenti ben visibili già in uso (Anagrafiche, Utenti, Aziende)
export const LINK = "text-blue-600 hover:underline";
export const STATO_COLORE = {
  aperta: "bg-sky-100 text-sky-800", vinta: "bg-emerald-100 text-emerald-800",
  persa: "bg-rose-100 text-rose-800", abbandonata: "bg-slate-200 text-slate-700",
};
export const LIVELLI = { nessuno: "Nessuno", proprie: "Proprie", proprie_e_libere: "Proprie e libere", tutte: "Tutte" };
export const TIPI_PIPELINE = { vendita: "Vendita", acquisizione: "Acquisizione", autonoma: "Autonoma" };
export const TIPI_FASE = {
  normale: "Normale", chiusura_da_collegare: "Chiusura da collegare",
  chiusura_collegata: "Chiusura collegata", chiusura: "Chiusura",
};

export function useCrmMe() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/crm/me").then((r) => setMe(r.data)).catch(() => setMe({ menu: false, flag: {}, livelli: {} })); }, []);
  return me;
}

export const haFlagCrm = (crmMe, companyId, flag) => !!crmMe?.flag?.[companyId]?.includes(flag);
export const gestioneQualsiasi = (crmMe) => Object.values(crmMe?.flag || {}).some((l) => l.some((f) => f.startsWith("crm_gestisci")));

export const errore = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : "Operazione non riuscita";
};

export function StatoBadge({ stato, testid }) {
  return <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${STATO_COLORE[stato] || ""}`}
    data-testid={testid}>{STATI_OPP[stato] || "—"}</span>;
}

export function ContatoreContatto({ conteggi, testid }) {
  if (!conteggi) return <span>—</span>;
  const { n, aperte } = conteggi;
  return <span className="text-xs text-slate-500" data-testid={testid}>
    {n} opportunità · {aperte} apert{aperte === 1 ? "a" : "e"}
  </span>;
}

// Barra schede scura, come nelle schede veicolo e pratica
export function BarraSchede({ schede, attiva, onScegli, testid }) {
  return (
    <div className="sticky top-0 z-20 bg-slate-900 rounded-md shadow-sm" data-testid={testid}>
      <div className="flex gap-1 overflow-x-auto" role="tablist">
        {schede.map((s) => (
          <button key={s.key} role="tab" aria-selected={attiva === s.key} onClick={() => onScegli(s.key)}
            className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-[3px] transition-colors ${attiva === s.key
              ? "border-sky-400 bg-slate-700 text-white font-semibold"
              : "border-transparent text-slate-200 font-medium hover:bg-slate-800 hover:text-white"}`}
            data-testid={`${testid}-${s.key}`}>{s.label}</button>
        ))}
      </div>
    </div>
  );
}

// Sprint CRM-1.1 — pulsante «torna» unico per tutte le pagine CRM
export function TornaIndietro({ to, testo, testid, onClick }) {
  return (
    <Button variant="outline" asChild className="border-[#0F4C81] text-[#0F4C81] hover:bg-sky-50" data-testid={testid}>
      <Link to={to} onClick={onClick}><ArrowLeft className="h-4 w-4 mr-1" />{testo}</Link>
    </Button>
  );
}

// Sprint CRM-2A.1 — date e ore italiane
export const fmtDataOra = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit",
  month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).replace(",", " alle") : "—");

export const TIPI_SOGGETTO = { privato: "Privato", azienda: "Azienda" };
export const TipoSoggetto = ({ tipo, testid }) => <span className="text-xs text-slate-500" data-testid={testid}>{TIPI_SOGGETTO[tipo] || "—"}</span>;

// Due pulsanti facoltativi, nessuno preselezionato; un secondo clic toglie la scelta
export function SceltaSoggetto({ value, onChange, prefix }) {
  return (
    <div className="flex gap-1" data-testid={prefix}>
      {Object.entries(TIPI_SOGGETTO).map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(value === k ? "" : k)} data-testid={`${prefix}-${k}`} aria-pressed={value === k}
          className={`px-3 py-1.5 rounded border text-sm transition-colors ${value === k ? "bg-sky-100 border-sky-500 text-sky-900 font-semibold" : "border-slate-200 hover:bg-slate-50"}`}>{l}</button>
      ))}
    </div>
  );
}

// Sprint CRM-2A.1 — pallino del titolare: colore FISSO ricavato dall'id, leggibile con il testo bianco
const TAVOLOZZA = ["#0F4C81", "#B91C1C", "#047857", "#7C3AED", "#C2410C", "#0E7490", "#BE185D", "#4D7C0F", "#1D4ED8", "#92400E"];
export function coloreUtente(id) {
  let h = 0;
  for (const ch of String(id || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TAVOLOZZA[h % TAVOLOZZA.length];
}
export const iniziali = (nome) => {
  const p = String(nome || "").trim().split(/\s+/).filter(Boolean);
  return (p.length > 1 ? p[0][0] + p[p.length - 1][0] : (p[0] || "").slice(0, 2)).toUpperCase();
};
export function Pallino({ id, nome, testid }) {
  if (!id) return <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400" title="Senza titolare" data-testid={testid}><User className="h-4 w-4" /></span>;
  return <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" title={nome || ""}
    style={{ backgroundColor: coloreUtente(id) }} data-testid={testid}>{iniziali(nome)}</span>;
}

// Sprint CRM-1.1 — il CRM lavora su UN'azienda alla volta; l'ultima scelta si ricorda nel browser
const LS_AZIENDA = "crm-azienda";
export function useAziendaCrm(crmMe) {
  const [cid, setCid] = useState(() => { try { return localStorage.getItem(LS_AZIENDA) || ""; } catch { return ""; } });
  const aziende = crmMe?.aziende || [];
  const valida = aziende.some((a) => a.id === cid) ? cid : aziende[0]?.id || "";
  const scegli = (id) => { setCid(id); try { localStorage.setItem(LS_AZIENDA, id); } catch {} };
  return [valida, scegli, aziende];
}

// Sprint CRM-2A — per ogni azienda si ricorda l'ultima pipeline; se non è più fra quelle accessibili, la prima
const lsPipeline = (cid) => `crm-pipeline-${cid}`;
export function pipelineScelta(cid, pipelines, richiesta) {
  const ok = (id) => !!id && pipelines.some((p) => p.id === id);
  if (ok(richiesta)) return richiesta;
  let mem = "";
  try { mem = localStorage.getItem(lsPipeline(cid)) || ""; } catch {}
  return ok(mem) ? mem : pipelines[0]?.id || "";
}
export const ricordaPipeline = (cid, pid) => { try { if (cid && pid) localStorage.setItem(lsPipeline(cid), pid); } catch {} };
// Sprint CRM-2A.1 — aprire una pipeline imposta anche la sua azienda
export const apriPipeline = (cid, pid) => { try { localStorage.setItem(LS_AZIENDA, cid); } catch {} ricordaPipeline(cid, pid); };

// Sprint CRM-2A — titolo come la pagina WEB; l'icona è quella della voce CRM del menu laterale
const IconaCrm = VOCI_MENU.find((v) => v.to === "/crm").icon;
export function TitoloCrm({ sotto }) {
  return (
    <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2 shrink-0" data-testid="crm-titolo">
      <IconaCrm className="h-6 w-6 text-blue-600" /> CRM{sotto && <span className="text-slate-400 font-normal">· {sotto}</span>}
    </h1>
  );
}

export const PillLead = ({ n }) => (
  <span className="rounded-full bg-sky-100 text-blue-700 px-3 py-0.5 text-sm font-semibold whitespace-nowrap" data-testid="crm-conteggio">
    {n == null ? "—" : n} lead</span>
);

export const Segnalazione = ({ children, testid }) => (
  <span className="inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-800" data-testid={testid}>{children}</span>
);

// Sprint CRM-5 — piccola etichetta «Azienda» sopra il campo, in tutte le intestazioni del CRM
const ConEtichetta = ({ children }) => (
  <div className="flex flex-col gap-0.5"><span className="text-[11px] text-slate-500" data-testid="crm-etichetta-azienda">Azienda</span>{children}</div>
);
export function CampoAzienda({ aziende, cid, onScegli }) {
  if (!aziende.length) return null;
  if (aziende.length === 1) {
    return <ConEtichetta><div className="h-10 px-3 flex items-center rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-800 min-w-[12rem]"
      data-testid="crm-azienda-unica">{aziende[0].nome}</div></ConEtichetta>;
  }
  return (
    <ConEtichetta><Select value={cid} onValueChange={onScegli}>
      <SelectTrigger className="w-56" data-testid="crm-azienda-select"><SelectValue /></SelectTrigger>
      <SelectContent>{aziende.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
    </Select></ConEtichetta>
  );
}

// Sprint CRM-5 — UNA formattazione dei telefoni in vista (il dato salvato resta con +39; tel: e WhatsApp usano il numero completo):
// cellulari italiani «333 123 4567», fissi italiani senza +39 e senza spazi, esteri con il loro prefisso
export function fmtTelefono(n) {
  if (!n) return "—";
  if (!n.startsWith("+39")) return n;
  const r = n.slice(3);
  return /^3\d{8,9}$/.test(r) ? `${r.slice(0, 3)} ${r.slice(3, 6)} ${r.slice(6)}` : r;
}

// Sprint CRM-5 — testo dell'etichetta bianco o scuro secondo la luminosità del colore (il giallo con testo scuro)
export function testoSu(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#ffffff";
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? "#0f172a" : "#ffffff";
}
export const stileEtichetta = (colore) => ({ backgroundColor: colore || "#64748b", color: testoSu(colore || "#64748b") });
